'use client';
import { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTrigger } from './ui/dialog';
import { Card } from './ui/card';
import { Search as SearchIcon, X } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

type SearchItem = { type: 'module' | 'scenario'; slug: string; title: string; href: string };

type SearchProps = {
  modules: Array<{ slug: string; title?: string }>;
  scenarios: Array<{ slug: string; title: string }>;
};

export function Search({ modules, scenarios }: SearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const router = useRouter();

  const items: SearchItem[] = useMemo(() => {
    return [
      ...modules.map((m) => ({
        type: 'module' as const,
        slug: m.slug,
        title: m.title || m.slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        href: `/modules/${m.slug}`,
      })),
      ...scenarios.map((s) => ({
        type: 'scenario' as const,
        slug: s.slug,
        title: s.title,
        href: `/scenarios/${s.slug}`,
      })),
    ];
  }, [modules, scenarios]);

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: ['title', 'slug'],
        threshold: 0.4,
        includeScore: true,
      }),
    [items]
  );

  const results = useMemo(() => {
    if (!query.trim()) return items.slice(0, 8);
    return fuse.search(query).map((r) => r.item);
  }, [query, fuse, items]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleSelect = (href: string) => {
    setOpen(false);
    setQuery('');
    router.push(href);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full justify-start min-w-0">
          <div className="flex items-center gap-1.5 text-muted-foreground/90 min-w-0">
            <SearchIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">Search or command…</span>
          </div>
          <kbd className="hidden xl:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 shrink-0 ml-1">
            <span className="text-xs">⌘</span>K
          </kbd>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        <div className="flex items-center border-b px-4 gap-2 w-full">
          <SearchIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            placeholder={
              query
                ? 'Search modules and scenarios...'
                : "Search or command... (e.g., 'Start ritual', 'Open memo', 'Run scenario')"
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 flex-1 min-w-0"
            autoFocus
          />
          {query && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setQuery('')}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-4">
          {results.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">No results found</div>
          ) : (
            <div className="space-y-2">
              {results.map((item) => (
                <Card
                  key={`${item.type}-${item.slug}`}
                  className="p-3 cursor-pointer hover:ring-1 ring-border transition-colors overflow-hidden"
                  onClick={() => handleSelect(item.href)}
                >
                  <div className="flex items-start justify-between gap-2 w-full">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{item.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 capitalize truncate">
                        {item.type} • {item.slug}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
